import React, { useState, useEffect } from 'react';
import { Search, Edit, Trash2, Eye, User, Mail, Calendar, Shield, BarChart3, Filter, Download, CheckSquare, Square, Settings } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { adminAPI } from '../../services/api';
import Card from '../common/Card';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import Button from '../common/Button';
import Input from '../common/Input';
import Modal from '../common/Modal';
import Table from '../common/Table';
import Badge from '../common/Badge';
import { formatDate } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkData, setBulkData] = useState({});
  const { loading, error, executeApiCall } = useApi();

  useEffect(() => {
    fetchUsers();
  }, [currentPage, search, roleFilter, statusFilter, sortBy, sortOrder, dateFrom, dateTo]);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        search: search,
        role: roleFilter,
        status: statusFilter,
        sortBy: sortBy,
        sortOrder: sortOrder,
        dateFrom: dateFrom,
        dateTo: dateTo
      });

      const result = await executeApiCall(
        () => adminAPI.getUsers({
          page: currentPage,
          limit: 10,
          search: search,
          role: roleFilter,
          status: statusFilter,
          sortBy: sortBy,
          sortOrder: sortOrder,
          dateFrom: dateFrom,
          dateTo: dateTo
        })
      );
      
      if (result.success) {
        setUsers(result.data.users);
        setPagination(result.data.pagination);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      username: user.username,
      email: user.email,
      role: user.role,
      is_active: user.is_active
    });
    setShowEditModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleViewDetails = async (user) => {
    try {
      const result = await executeApiCall(
        () => adminAPI.getUserDetails(user.id)
      );
      
      if (result.success) {
        setSelectedUser(result.data);
        setShowDetailsModal(true);
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  };

  const handleUpdateUser = async () => {
    try {
      const result = await executeApiCall(
        () => adminAPI.updateUser(selectedUser.id, editForm)
      );
      
      if (result.success) {
        setShowEditModal(false);
        fetchUsers();
      }
    } catch (err) {
      console.error('Error updating user:', err);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await executeApiCall(
        () => adminAPI.deleteUser(selectedUser.id)
      );
      
      if (result.success) {
        setShowDeleteModal(false);
        fetchUsers();
      }
    } catch (err) {
      console.error('Error deleting user:', err);
    }
  };

  const handleBulkAction = async () => {
    try {
      const result = await executeApiCall(
        () => adminAPI.bulkUpdateUsers({
          userIds: selectedUsers,
          action: bulkAction,
          data: bulkData
        }).then(res => res.data)
      );
      
      if (result.success) {
        setShowBulkModal(false);
        setSelectedUsers([]);
        setBulkAction('');
        setBulkData({});
        fetchUsers();
      }
    } catch (err) {
      console.error('Error performing bulk action:', err);
    }
  };

  const handleExportUsers = async (format = 'csv') => {
    try {
      const params = new URLSearchParams({
        format: format,
        filters: JSON.stringify({
          search: search,
          role: roleFilter,
          status: statusFilter
        })
      });

      const response = await adminAPI.exportUsers({
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting users:', err);
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  const handleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'red',
      user: 'blue'
    };
    return <Badge color={colors[role] || 'gray'} text={role} />;
  };

  const getStatusBadge = (isActive) => {
    return isActive ? 
      <Badge color="green" text="Active" /> : 
      <Badge color="gray" text="Inactive" />;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <Alert type="error" message={error} />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">User management</h1>
        <p className="mt-1.5 text-sm text-ink-500">Search, filter and edit users across the platform.</p>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search users by username or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                icon={Search}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExportUsers('csv')}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-ink-200">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Role</label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                >
                  <option value="">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                >
                  <option value="created_at">Created Date</option>
                  <option value="username">Username</option>
                  <option value="email">Email</option>
                  <option value="role">Role</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                >
                  <option value="DESC">Descending</option>
                  <option value="ASC">Ascending</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Date From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Date To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                />
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Users Table */}
      <Card>
        {selectedUsers.length > 0 && (
          <div className="flex items-center justify-between p-4 bg-blue-50 border-b border-blue-200">
            <span className="text-sm text-blue-700">
              {selectedUsers.length} user(s) selected
            </span>
            <div className="flex space-x-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBulkModal(true)}
              >
                Bulk Actions
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedUsers([])}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        )}
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Header>
                <input
                  type="checkbox"
                  checked={selectedUsers.length === users.length && users.length > 0}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-ink-300 rounded"
                />
              </Table.Header>
              <Table.Header>User</Table.Header>
              <Table.Header>Role</Table.Header>
              <Table.Header>Status</Table.Header>
              <Table.Header>Created</Table.Header>
              <Table.Header>Actions</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {users.map((user) => (
              <Table.Row key={user.id}>
                <Table.Cell>
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => handleSelectUser(user.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-ink-300 rounded"
                  />
                </Table.Cell>
                <Table.Cell>
                  <div>
                    <p className="font-medium text-ink-900">{user.username}</p>
                    <p className="text-sm text-ink-500">{user.email}</p>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  {getRoleBadge(user.role)}
                </Table.Cell>
                <Table.Cell>
                  {getStatusBadge(user.is_active)}
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-ink-500">
                    {formatDate(user.created_at)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewDetails(user)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      title="View Analytics"
                      onClick={() => navigate(`/admin/users/${user.id}/analytics`)}
                    >
                      <BarChart3 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditUser(user)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      color="red"
                      onClick={() => handleDeleteUser(user)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-between items-center mt-4">
            <p className="text-sm text-ink-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} users
            </p>
            <div className="flex space-x-2">
              <Button
                size="sm"
                variant="outline"
                disabled={pagination.page === 1}
                onClick={() => setCurrentPage(pagination.page - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={pagination.page === pagination.pages}
                onClick={() => setCurrentPage(pagination.page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Edit User Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit User"
      >
        <div className="space-y-4">
          <Input
            label="Username"
            value={editForm.username}
            onChange={(e) => setEditForm({...editForm, username: e.target.value})}
          />
          <Input
            label="Email"
            type="email"
            value={editForm.email}
            onChange={(e) => setEditForm({...editForm, email: e.target.value})}
          />
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Role
            </label>
            <select
              value={editForm.role}
              onChange={(e) => setEditForm({...editForm, role: e.target.value})}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={editForm.is_active}
              onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-ink-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-ink-900">
              Active
            </label>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="outline" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpdateUser}>
            Update User
          </Button>
        </div>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete User"
      >
        <p className="text-ink-600">
          Are you sure you want to delete user <strong>{selectedUser?.username}</strong>? 
          This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDeleteConfirm}>
            Delete User
          </Button>
        </div>
      </Modal>

      {/* User Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="User Details"
        size="lg"
      >
        {selectedUser && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-ink-900 mb-3">User Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <User className="h-4 w-4 text-ink-400 mr-2" />
                    <span className="text-sm text-ink-600">Username:</span>
                    <span className="text-sm font-medium ml-2">{selectedUser.user?.username}</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 text-ink-400 mr-2" />
                    <span className="text-sm text-ink-600">Email:</span>
                    <span className="text-sm font-medium ml-2">{selectedUser.user?.email}</span>
                  </div>
                  <div className="flex items-center">
                    <Shield className="h-4 w-4 text-ink-400 mr-2" />
                    <span className="text-sm text-ink-600">Role:</span>
                    <span className="ml-2">{getRoleBadge(selectedUser.user?.role)}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-ink-400 mr-2" />
                    <span className="text-sm text-ink-600">Created:</span>
                    <span className="text-sm font-medium ml-2">
                      {formatDate(selectedUser.user?.created_at)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-ink-900 mb-3">Usage Statistics</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-ink-600">Total Links:</span>
                    <span className="text-sm font-medium">{selectedUser.links?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-ink-600">Total Clicks:</span>
                    <span className="text-sm font-medium">{selectedUser.analytics?.total_clicks || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-ink-600">Unique Visitors:</span>
                    <span className="text-sm font-medium">{selectedUser.analytics?.unique_visitors || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {selectedUser.links && selectedUser.links.length > 0 && (
              <div>
                <h4 className="font-medium text-ink-900 mb-3">User Links</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedUser.links.map((link) => (
                    <div key={link.id} className="flex justify-between items-center p-2 bg-ink-50 rounded">
                      <div>
                        <p className="text-sm font-medium">{link.title || link.original_url}</p>
                        <p className="text-xs text-ink-500">{link.short_code}</p>
                      </div>
                      <span className="text-xs text-ink-500">{link.clicks} clicks</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            onClick={() => {
              setShowDetailsModal(false);
              navigate(`/admin/users/${selectedUser.user?.id}/analytics`);
            }}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            View Analytics
          </Button>
          <Button onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </div>
      </Modal>

      {/* Bulk Actions Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title="Bulk Actions"
      >
        <div className="space-y-4">
          <p className="text-ink-600">
            Perform actions on {selectedUsers.length} selected user(s)
          </p>
          
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Action
            </label>
            <select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
            >
              <option value="">Select Action</option>
              <option value="activate">Activate Users</option>
              <option value="deactivate">Deactivate Users</option>
              <option value="delete">Delete Users</option>
              <option value="update">Update Users</option>
            </select>
          </div>

          {bulkAction === 'update' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Role</label>
                <select
                  value={bulkData.role || ''}
                  onChange={(e) => setBulkData({...bulkData, role: e.target.value})}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                >
                  <option value="">No Change</option>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="bulk_is_active"
                  checked={bulkData.is_active !== undefined ? bulkData.is_active : false}
                  onChange={(e) => setBulkData({...bulkData, is_active: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-ink-300 rounded"
                />
                <label htmlFor="bulk_is_active" className="ml-2 block text-sm text-ink-900">
                  Set Active Status
                </label>
              </div>
            </div>
          )}

          {bulkAction === 'delete' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> This action will permanently delete {selectedUsers.length} user(s) and cannot be undone.
              </p>
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="outline" onClick={() => setShowBulkModal(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkAction}
            disabled={!bulkAction}
            color={bulkAction === 'delete' ? 'red' : undefined}
          >
            {bulkAction === 'activate' && 'Activate Users'}
            {bulkAction === 'deactivate' && 'Deactivate Users'}
            {bulkAction === 'delete' && 'Delete Users'}
            {bulkAction === 'update' && 'Update Users'}
            {!bulkAction && 'Select Action'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default UserManagement;
